import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Phone } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  SearchDropdown,
  type SearchDropdownItem,
} from '@/components/ui/search-dropdown'
import {
  companiesClient,
  type AvailablePhoneNumber,
} from '@/lib/api/companies.client'

interface ProvisionPhoneNumberModalProps {
  open: boolean
  companyId: string
  companyName: string
  onOpenChange: (open: boolean) => void
  onProvisioned: (phoneNumber: string) => void
}

type Step = 'search' | 'confirm'

export function ProvisionPhoneNumberModal({
  open,
  companyId,
  companyName,
  onOpenChange,
  onProvisioned,
}: ProvisionPhoneNumberModalProps) {
  const [step, setStep] = useState<Step>('search')
  const [selected, setSelected] = useState<AvailablePhoneNumber | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) {
      setStep('search')
      setSelected(null)
      setSubmitting(false)
    }
  }, [open])

  const fetchItems = useCallback(
    async (
      query?: string,
    ): Promise<SearchDropdownItem<AvailablePhoneNumber>[]> => {
      const trimmed = (query ?? '').trim()
      if (!trimmed) return []

      const isAreaCode = /^\d{3}$/.test(trimmed)
      try {
        const { numbers } = await companiesClient.listAvailablePhoneNumbers(
          companyId,
          isAreaCode ? { areaCode: trimmed } : { locality: trimmed },
        )
        return numbers.map(n => ({
          id: n.phoneNumber,
          label: n.friendlyName,
          sublabel:
            [n.locality, n.region].filter(Boolean).join(', ') || n.isoCountry,
          data: n,
        }))
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to fetch numbers',
        )
        return []
      }
    },
    [companyId],
  )

  const handlePick = (
    item: SearchDropdownItem<AvailablePhoneNumber> | null,
  ) => {
    if (!item?.data) return
    setSelected(item.data)
    setStep('confirm')
  }

  const handleProvision = async () => {
    if (!selected || submitting) return
    setSubmitting(true)
    try {
      const result = await companiesClient.provisionPhoneNumber(
        companyId,
        selected.phoneNumber,
      )
      toast.success(`Provisioned ${result.phoneNumber}`)
      onProvisioned(result.phoneNumber)
      onOpenChange(false)
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to provision number'
      toast.error(message)
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {step === 'search' ? 'Provision a phone number' : 'Confirm number'}
          </DialogTitle>
          <DialogDescription>
            {step === 'search'
              ? 'Type a 3-digit area code or a city name to find available numbers. Voice only for now — SMS support is coming.'
              : `Super will provision this number for ${companyName} for inbound voice calls. SMS is not enabled yet.`}
          </DialogDescription>
        </DialogHeader>

        {step === 'search' ? (
          <div className="space-y-4">
            <SearchDropdown<AvailablePhoneNumber>
              fetchItems={fetchItems}
              loadInitialItems={false}
              placeholder="Search area code (e.g. 415) or city (e.g. San Francisco)"
              searchPlaceholder="Area code or city..."
              emptyMessage="Type an area code or city to search"
              onSelect={item =>
                handlePick(
                  (item as SearchDropdownItem<AvailablePhoneNumber>) || null,
                )
              }
              renderItem={item => {
                const number = item.data as AvailablePhoneNumber | undefined
                return (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{item.label}</div>
                      {item.sublabel && (
                        <div className="text-xs text-muted-foreground truncate">
                          {item.sublabel}
                        </div>
                      )}
                    </div>
                    {number?.capabilities.voice && (
                      <div className="flex gap-1 shrink-0">
                        <Phone className="h-3 w-3" aria-label="Voice" />
                      </div>
                    )}
                  </>
                )
              }}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
            </DialogFooter>
          </div>
        ) : (
          selected && (
            <div className="space-y-4">
              <div className="rounded-md border p-4 space-y-2">
                <div className="text-lg font-medium">
                  {selected.friendlyName}
                </div>
                <div className="text-sm text-muted-foreground">
                  {[selected.locality, selected.region, selected.isoCountry]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
                <div className="flex gap-2 pt-1">
                  {selected.capabilities.voice && (
                    <Badge variant="secondary">Voice</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Voice only — SMS is not enabled yet.
                </p>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep('search')}
                  disabled={submitting}
                >
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={handleProvision}
                  disabled={submitting}
                >
                  {submitting ? 'Provisioning...' : 'Provision this number'}
                </Button>
              </DialogFooter>
            </div>
          )
        )}
      </DialogContent>
    </Dialog>
  )
}
